<script setup lang="ts">
import {computed, nextTick, onMounted, ref, watch} from 'vue'
import type {MaybeRef} from 'vue'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import LoadingComponent from '@toolify/client/src/modules/ui/components/LoadingComponent.vue'
import {useKeyboardShortcuts} from '@toolify/client/src/modules/keyboard/composables/useKeyboardShortcuts'
import {KeyboardSpecialKeyTypes} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardSpecialKeyTypes'
import DropdownComponent from '@toolify/client/src/modules/modals/components/DropdownComponent.vue'
import InfinityScrollComponent from '@toolify/client/src/modules/ui/components/InfinityScrollComponent.vue'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {useI18n} from 'vue-i18n'

interface SuggestionSnippetPart {
  text: string
  isHit: boolean
}

interface SuggestionItem {
  id: string
  label: string
  description?: string
  typeLabel?: string
  /** Leading visual for the row; the thumbnail image wins over the icon when both are given. */
  imageUrl?: string
  iconName?: IconNameType
  /** Icon shown before the description — where the suggestion lives (its parent resource). */
  descriptionIconName?: IconNameType
  /** Why this suggestion matched: a fragment of the underlying content with the hits marked. */
  snippetParts?: SuggestionSnippetPart[]
}

interface Props {
  modelValue?: string
  placeholder?: string
  suggestions?: SuggestionItem[]
  activeSuggestionIndex?: number
  autoFocus?: boolean
  idleListCaption?: string
  stackReferenceElement?: MaybeRef<HTMLElement | null>
  isLoadingSuggestions?: boolean
  /** More pages exist beyond the loaded suggestions — scrolling to the end asks the owner for the next one. */
  hasMoreSuggestions?: boolean
  isLoadingMoreSuggestions?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: string): void
  (e: 'update:activeSuggestionIndex', value: number): void
  (e: 'selectSuggestion', suggestion: SuggestionItem): void
  (e: 'submit', value: string): void
  (e: 'blur'): void
  (e: 'loadMore'): void
}

const {t} = useI18n()

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: '',
  suggestions: () => [],
  activeSuggestionIndex: -1,
  autoFocus: false,
  idleListCaption: '',
  isLoadingSuggestions: false,
  hasMoreSuggestions: false,
  isLoadingMoreSuggestions: false,
})

const emit = defineEmits<Emits>()

const editableRef = ref<HTMLDivElement | null>(null)
const isFocused = ref(false)

/** Thumbnails whose URL failed to load — those rows fall back to their icon instead of a broken-image glyph. */
const failedImageUrls = ref(new Set<string>())
const dropdownDismissedAfterCommit = ref(false)

const {getThemeVariable} = useThemeVariables()

function onLoadMoreVisibilityChange(isVisible: boolean) {
  if(isVisible) {
    emit('loadMore')
  }
}

/**
 * The scroll component needs an ancestor with a real height — `height: 100%` inside it resolves
 * against nothing under a max-height-only parent, and the list just spills without a scrollbar. The
 * fixed height is applied only when a search has enough rows to need scrolling, so short result
 * lists and the idle recents keep hugging their content.
 */
const isPagedList = computed(() => {
  return !!trimmedValue.value && props.suggestions.length >= 8
})

const trimmedValue = computed(() => props.modelValue.trim())
const shouldShowSearchAction = computed(() => {
  return !!trimmedValue.value
})
const searchActionIndex = computed(() => {
  return props.suggestions.length
})
const availableOptionsCount = computed(() => {
  return props.suggestions.length + (shouldShowSearchAction.value ? 1 : 0)
})
const resolvedActiveOptionIndex = computed(() => {
  if (props.activeSuggestionIndex < 0 || props.activeSuggestionIndex >= availableOptionsCount.value) {
    if (props.suggestions.length) {
      return 0
    }
    if (!trimmedValue.value) {
      return -1
    }
    return shouldShowSearchAction.value ? searchActionIndex.value : -1
  }
  return props.activeSuggestionIndex
})
const resolvedActiveSuggestionIndex = computed(() => {
  if (resolvedActiveOptionIndex.value >= props.suggestions.length) {
    return -1
  }
  return resolvedActiveOptionIndex.value
})
const activeSuggestion = computed(() => {
  if (resolvedActiveSuggestionIndex.value < 0) {
    return null
  }
  return props.suggestions[resolvedActiveSuggestionIndex.value]
})

const suggestionSuffix = computed(() => {
  const suggestion = activeSuggestion.value
  if (!suggestion || !trimmedValue.value) {
    return ''
  }
  const normalizedValue = trimmedValue.value.toLocaleLowerCase()
  const normalizedLabel = suggestion.label.toLocaleLowerCase()
  if (!normalizedLabel.startsWith(normalizedValue) || suggestion.label.length <= trimmedValue.value.length) {
    return ''
  }
  return suggestion.label.slice(trimmedValue.value.length)
})

const shouldShowSuggestions = computed(() => {
  return !!trimmedValue.value.length && !!props.suggestions.length
})
const shouldShowDropdown = computed(() => {
  if (dropdownDismissedAfterCommit.value) {
    return false
  }
  if (trimmedValue.value.length) {
    return !!props.suggestions.length || shouldShowSearchAction.value
  }
  if (!isFocused.value) {
    return false
  }
  return props.suggestions.length > 0
})

function getEditableText() {
  if (!editableRef.value) {
    return ''
  }
  return Array.from(editableRef.value.childNodes).map(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || ''
    }
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      !(node as HTMLElement).dataset.suggestionSuffix
    ) {
      return node.textContent || ''
    }
    return ''
  }).join('').replace(/\n/g, '')
}

function syncEditableValue() {
  if (!editableRef.value) {
    return
  }
  const currentText = getEditableText()
  if (currentText === props.modelValue) {
    renderEditableContent()
    return
  }
  renderEditableContent()
}

function renderEditableContent() {
  if (!editableRef.value) {
    return
  }
  editableRef.value.replaceChildren()
  if (props.modelValue) {
    editableRef.value.appendChild(document.createTextNode(props.modelValue))
  }
  if (suggestionSuffix.value) {
    const suffixElement = document.createElement('span')
    suffixElement.dataset.suggestionSuffix = 'true'
    suffixElement.contentEditable = 'false'
    suffixElement.className = 'search-input__editable-suffix'
    suffixElement.textContent = suggestionSuffix.value
    editableRef.value.appendChild(suffixElement)
  }
}

function focusEditable() {
  nextTick(() => {
    if (!editableRef.value) {
      return
    }
    editableRef.value.focus()
    const selection = window.getSelection()
    const range = document.createRange()
    const textNode = Array.from(editableRef.value.childNodes).find(node => {
      return node.nodeType === Node.TEXT_NODE
    })
    if (textNode) {
      range.setStart(textNode, textNode.textContent?.length || 0)
      range.collapse(true)
    } else {
      range.selectNodeContents(editableRef.value)
      range.collapse(false)
    }
    selection?.removeAllRanges()
    selection?.addRange(range)
  })
}

function setModelValue(value: string) {
  emit('update:modelValue', value.replace(/\n/g, ''))
}

function onInput() {
  dropdownDismissedAfterCommit.value = false
  const value = getEditableText()
  setModelValue(value)
  emit('update:activeSuggestionIndex', -1)
}

function onSuggestionClick(suggestion: SuggestionItem, index: number) {
  dropdownDismissedAfterCommit.value = true
  emit('update:activeSuggestionIndex', index)
  setModelValue(suggestion.label)
  emit('selectSuggestion', suggestion)
}

function onTabComplete() {
  if (!activeSuggestion.value) {
    return
  }
  dropdownDismissedAfterCommit.value = true
  setModelValue(activeSuggestion.value.label)
  emit('update:activeSuggestionIndex', -1)
  focusEditable()
}

function onSubmit() {
  if (resolvedActiveOptionIndex.value === searchActionIndex.value && shouldShowSearchAction.value) {
    dropdownDismissedAfterCommit.value = true
    emit('submit', trimmedValue.value)
    return
  }
  if (activeSuggestion.value) {
    dropdownDismissedAfterCommit.value = true
    setModelValue(activeSuggestion.value.label)
    emit('selectSuggestion', activeSuggestion.value)
    return
  }
  if (trimmedValue.value.length) {
    dropdownDismissedAfterCommit.value = true
  }
  emit('submit', trimmedValue.value)
}

function selectNextSuggestion() {
  if (!availableOptionsCount.value) {
    return
  }
  const nextIndex = props.activeSuggestionIndex >= availableOptionsCount.value - 1
    ? 0
    : props.activeSuggestionIndex + 1
  emit('update:activeSuggestionIndex', nextIndex)
}

function selectPreviousSuggestion() {
  if (!availableOptionsCount.value) {
    return
  }
  const previousIndex = props.activeSuggestionIndex <= 0
    ? availableOptionsCount.value - 1
    : props.activeSuggestionIndex - 1
  emit('update:activeSuggestionIndex', previousIndex)
}

function onSearchActionMouseDown() {
  dropdownDismissedAfterCommit.value = true
  emit('submit', trimmedValue.value)
}

useKeyboardShortcuts([
  [[KeyboardSpecialKeyTypes.Down], () => selectNextSuggestion()],
  [[KeyboardSpecialKeyTypes.Up], () => selectPreviousSuggestion()],
  [[KeyboardSpecialKeyTypes.Tab], () => onTabComplete()],
  [[KeyboardSpecialKeyTypes.Enter], () => onSubmit()],
], () => isFocused.value)

watch(trimmedValue, (value) => {
  if (!value) {
    dropdownDismissedAfterCommit.value = false
  }
})

watch(() => props.modelValue, () => {
  syncEditableValue()
  if (isFocused.value) {
    focusEditable()
  }
})

/** The list scrolls now, so keyboard navigation must bring the active row into view — it used to always be visible. */
const suggestionListRef = ref<HTMLDivElement | null>(null)
watch(() => props.activeSuggestionIndex, (index) => {
  if(index < 0) {
    return
  }
  void nextTick(() => {
    suggestionListRef.value
      ?.querySelectorAll('.suggestion-list__item')[index]
      ?.scrollIntoView({block: 'nearest'})
  })
})

watch(() => props.activeSuggestionIndex, () => {
  if (isFocused.value) {
    focusEditable()
  }
})

onMounted(() => {
  syncEditableValue()
})

watch(() => props.autoFocus, (enabled) => {
  if (enabled) {
    focusEditable()
  }
}, {immediate: true})

defineExpose({
  focus: focusEditable,
})
</script>

<template>
  <div class="search-input">
    <DropdownComponent
      :isOpen="shouldShowDropdown"
      placement="bottom-start"
      :offsetMainAxis="6"
      :matchReferenceSize="true"
      :shouldCaptureKeyboard="false"
      :stackReferenceElement="stackReferenceElement"
      class="search-input__dropdown"
    >
      <template #default>
        <div class="search-input__field">
          <IconComponent :name="IconNameType.Search" :size="SizeVariantType.Small" :color="ColorVariantType.Dim"/>
          <div class="search-input__editable-shell">
            <div
              ref="editableRef"
              contenteditable="true"
              spellcheck="false"
              class="search-input__editable"
              :data-placeholder="placeholder"
              :data-empty="!modelValue"
              @input="onInput"
              @focus="isFocused = true"
              @blur="isFocused = false; emit('blur')"
            />
          </div>
        </div>
      </template>

      <template #dropdown>
        <div
          class="suggestion-list"
          :class="{'suggestion-list--paged': isPagedList}"
          ref="suggestionListRef"
        >
          <InfinityScrollComponent
            class="suggestion-list__scroll"
            :shouldShowLoadMore="!!trimmedValue && hasMoreSuggestions && !!suggestions.length"
            :isLoading="isLoadingMoreSuggestions"
            :gradientColor="getThemeVariable(ThemeVariableType.ContainerLowestColor)"
            @loadMoreVisibilityChange="onLoadMoreVisibilityChange"
          >
            <div class="suggestion-list__items">
              <div
                v-if="!trimmedValue && idleListCaption && suggestions.length"
                class="suggestion-list__caption"
              >
                {{idleListCaption}}
              </div>
              <button
                v-for="(suggestion, index) in suggestions"
                :key="suggestion.id"
                type="button"
                class="suggestion-list__item"
                :class="{'is-active': index === resolvedActiveSuggestionIndex}"
                @mouseenter="emit('update:activeSuggestionIndex', index)"
                @mousedown.prevent="onSuggestionClick(suggestion, index)"
              >
                <div v-if="suggestion.imageUrl || suggestion.iconName" class="suggestion-list__leading">
                  <img
                    v-if="suggestion.imageUrl && !failedImageUrls.has(suggestion.imageUrl)"
                    :src="suggestion.imageUrl"
                    class="suggestion-list__leading-image"
                    alt=""
                    @error="failedImageUrls.add(suggestion.imageUrl)"
                  />

                  <IconComponent v-else-if="suggestion.iconName" :name="suggestion.iconName"/>
                </div>
                <div class="suggestion-list__content">
                  <div class="suggestion-list__label">{{suggestion.label}}</div>
                  <!-- Interpolation on purpose: the snippet is user file content, never markup. -->
                  <div class="suggestion-list__snippet" v-if="suggestion.snippetParts?.length">
                    <template v-for="(part, partIndex) in suggestion.snippetParts" :key="partIndex">
                      <span v-if="part.isHit" class="suggestion-list__snippet-hit">{{part.text}}</span>
                      <template v-else>{{part.text}}</template>
                    </template>
                  </div>
                  <div class="suggestion-list__description" v-if="suggestion.description || suggestion.typeLabel">
                    <IconComponent
                      v-if="suggestion.description && suggestion.descriptionIconName"
                      :name="suggestion.descriptionIconName"
                      :size="SizeVariantType.XSmall"
                      class="suggestion-list__description-icon"
                    />
                    <span v-if="suggestion.description">{{suggestion.description}}</span>
                    <span v-if="suggestion.description && suggestion.typeLabel">•</span>
                    <span v-if="suggestion.typeLabel">{{suggestion.typeLabel}}</span>
                  </div>
                </div>
              </button>
              <div
                v-if="trimmedValue && isLoadingSuggestions && !suggestions.length"
                class="suggestion-list__status suggestion-list__status--loading"
              >
                <LoadingComponent :size="18"/>
                <span class="suggestion-list__status-text">{{t('general.loading')}}</span>
              </div>
              <div
                v-else-if="trimmedValue && !isLoadingSuggestions && !suggestions.length"
                class="suggestion-list__status suggestion-list__status--empty"
              >
                <div class="suggestion-list__empty-icon-wrap">
                  <IconComponent
                    :name="IconNameType.Search"
                    :size="SizeVariantType.Medium"
                    :color="ColorVariantType.Dim"
                    :weightType="IconWeightType.Solid"
                  />
                </div>
                <div class="suggestion-list__empty-title">{{t('general.noResultsFound')}}</div>
                <div class="suggestion-list__empty-hint">{{t('general.noSuggestions')}}</div>
              </div>
              <div
                v-else-if="!trimmedValue && !suggestions.length"
                class="suggestion-list__status suggestion-list__status--idle"
              >
                <div class="suggestion-list__empty-hint suggestion-list__empty-hint--idle">{{t('general.searchStartTypingDescription')}}</div>
              </div>
              <button
                v-if="shouldShowSearchAction && false"
                type="button"
                class="suggestion-list__item suggestion-list__search-action"
                :class="{'is-active': resolvedActiveOptionIndex === searchActionIndex}"
                @mouseenter="emit('update:activeSuggestionIndex', searchActionIndex)"
                @mousedown.prevent="onSearchActionMouseDown"
              >
                <div class="suggestion-list__content">
                  <div class="suggestion-list__label">
                    Zamiast tego wyszukaj
                    <span class="suggestion-list__search-action-keyword">"{{trimmedValue}}"</span>
                  </div>
                </div>
              </button>
            </div>
          </InfinityScrollComponent>
        </div>
      </template>
    </DropdownComponent>
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;

.search-input {
  display: grid;
  width: min(100%, 36rem);

  @media (max-width: 960px) {
    width: 100%;
    max-width: min(100%, 36rem);
    min-width: 0;
  }
}

.search-input__dropdown {
  width: 100%;
}

.search-input__field {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-2);
  min-height: map-get(variables.$sizes, size-xl);
  padding: 0 map-get(variables.$spacing, space-3);
  border-radius: calc(variables.$borderRadius);
  background: map-get(variables.$colors, container-lowest);
}

.search-input__editable-shell {
  position: relative;
  flex: 1;
  min-width: 0;
}

.search-input__editable {
  min-width: 0;
  min-height: map-get(variables.$sizes, size-sm);
  outline: none;
  border: 0;
  color: map-get(variables.$colors, content-normal);
  white-space: pre;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: map-get(variables.$textSizes, text-base);
  line-height: 1.5rem;

  &[data-empty='true']::before {
    content: attr(data-placeholder);
    color: map-get(variables.$colors, content-dim);
    pointer-events: none;
  }
}

:deep(.search-input__editable-suffix) {
  color: map-get(variables.$colors, content-dim);
  user-select: none;
  pointer-events: none;
}

/**
 * Mirrors the notification/activity containers: the scroll component works only under an ancestor
 * with a REAL height (its inner `height: 100%` chain resolves against nothing under max-height
 * alone). The height arrives with the `--paged` state — a searching list long enough to scroll —
 * while short lists and the idle recents keep hugging their content.
 */
.suggestion-list {
  display: flex;
  flex-direction: column;
  background: map-get(variables.$colors, container-lowest);

  &.suggestion-list--paged {
    height: min(60vh, 480px);
  }
}

.suggestion-list__scroll {
  flex: 1;
  min-height: 0;
}

.suggestion-list__items {
  display: grid;
  gap: map-get(variables.$spacing, space-1);
  padding: map-get(variables.$spacing, space-1-5);
}

.suggestion-list__status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: map-get(variables.$spacing, space-2);
  min-height: 5.5rem;
  padding: 1rem 0.75rem 1.125rem;
  text-align: center;
  box-sizing: border-box;
}

.suggestion-list__status--loading {
  flex-direction: row;
  justify-content: center;
  gap: 0.625rem;
  min-height: 4rem;
  color: map-get(variables.$colors, content-dim);
  font-size: map-get(variables.$textSizes, text-sm);
}

.suggestion-list__status-text {
  font-weight: 500;
}

.suggestion-list__empty-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: map-get(variables.$sizes, size-2xl);
  height: map-get(variables.$sizes, size-2xl);
  border-radius: map-get(variables.$radiuses, radius-full);
  background: map-get(variables.$colors, container-low);
  color: map-get(variables.$colors, content-dim);
}

.suggestion-list__empty-title {
  font-size: map-get(variables.$textSizes, text-base);
  font-weight: 600;
  line-height: 1.3;
  color: map-get(variables.$colors, content-normal);
}

.suggestion-list__empty-hint {
  font-size: map-get(variables.$textSizes, text-sm);
  line-height: 1.45;
  color: map-get(variables.$colors, content-dim);
  max-width: 18rem;
}

.suggestion-list__empty-hint--idle {
  max-width: 20rem;
  font-size: map-get(variables.$textSizes, text-sm);
}

.suggestion-list__caption {
  padding: map-get(variables.$spacing, space-1-5) map-get(variables.$spacing, space-2) map-get(variables.$spacing, space-0-5);
  font-size: map-get(variables.$textSizes, text-xs);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: map-get(variables.$colors, content-dim);
}

.suggestion-list__item {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 0;
  border-radius: variables.$borderRadius;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &.is-active {
    background: map-get(variables.$colors, container-low);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: map-get(variables.$colors, container-low);
    }
  }
}

.suggestion-list__content {
  display: grid;
  gap: map-get(variables.$spacing, space-0-5);
  min-width: 0;
  flex: 1;
}

.suggestion-list__leading {
  flex-shrink: 0;
  width: map-get(variables.$sizes, size-xl);
  height: map-get(variables.$sizes, size-xl);
  border-radius: map-get(variables.$radiuses, radius-lg);
  background: map-get(variables.$colors, container);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: map-get(variables.$textSizes, text-lg);
  color: map-get(variables.$colors, content-normal);
}

.suggestion-list__leading-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.suggestion-list__description-icon {
  flex-shrink: 0;
}

.suggestion-list__label {
  color: map-get(variables.$colors, content-normal);
  font-weight: 500;
}

.suggestion-list__description {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-1);
  min-width: 0;
  color: map-get(variables.$colors, content-dim);
  font-size: map-get(variables.$textSizes, text-sm);
}

  .suggestion-list__snippet {
    font-size: map-get(variables.$textSizes, text-xs);
    color: map-get(variables.$colors, 'content-dim');
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .suggestion-list__snippet-hit {
    font-weight: 600;
    color: map-get(variables.$colors, 'on-primary-container');
    background: map-get(variables.$colors, 'primary-container');
    border-radius: map-get(variables.$radiuses, radius-sm);
    padding: 0 map-get(variables.$spacing, space-0-5);
  }

.suggestion-list__search-action {
  margin-top: 0.125rem;

  .suggestion-list__label {
    color: map-get(variables.$colors, content-dim);
  }
  
}

.suggestion-list__search-action-keyword {
  font-weight: 600;
  color: map-get(variables.$colors, content-normal);
}
</style>
